import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, 
  TrendingUp, 
  Monitor, 
  Database, 
  Brain, 
  Lightbulb, 
  FileText, 
  Users,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const services = [
  {
    icon: Target,
    title: 'Strategy & Operational Efficiency',
    description: "When performance drifts, it's usually because priorities and processes aren't aligned.",
    items: [
      'Strengthening corporate strategy and execution plans',
      'Improve operating models and role clarity',
      'Redesign processes to reduce cycle time and waste',
      'Document policies and procedures that actually get used',
      'Deliver cost reduction strategies tied to measurable targets',
    ],
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    icon: Users,
    title: 'Organizational Consulting & Change Management',
    description: "Change fails when people aren't supported, informed, and empowered.",
    items: [
      'Build change management plans that drive adoption',
      'Improve organizational design and development',
      'Align structure to strategy and service delivery',
      'Provide staff augmentation that fits governance and delivery needs',
    ],
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    icon: TrendingUp,
    title: 'Finance Advisory & Performance Management',
    description: "Strong financial management is not just about numbers—it's about control and accountability.",
    items: [
      'Improve budgeting and forecasting',
      'Define performance measures and management reporting',
      'Build KPI frameworks that leadership trusts',
      'Strengthen performance management and accountability',
      'Support enterprise risk management and internal control improvement',
    ],
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    icon: FileText,
    title: 'Grants and Contracts Support',
    description: 'Grants and contracts can be a growth engine—or a compliance headache.',
    items: [
      'Strengthen contracts management processes',
      'Improve tracking, reporting, and documentation',
      'Reduce risk through clear controls and ownership',
      'Support audit readiness and compliance alignment',
    ],
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: Monitor,
    title: 'Technology Consulting & Digital Transformation',
    description: 'Technology should reduce burden—not create it.',
    items: [
      'Define IT strategy and governance',
      'Support PMO and delivery discipline',
      'Plan and execute digital transformation initiatives',
      'Lead data analytics and migration planning',
      'Support ERP selection and implementation readiness',
    ],
    gradient: 'from-blue-500 to-indigo-500',
  },
  {
    icon: Database,
    title: 'Data Readiness (Preparation, Sanitation, Validation)',
    description: 'If your data is unreliable, every downstream decision is shaky.',
    items: [
      'Prepare and standardize data for reporting and analytics',
      'Clean and validate data using repeatable rules',
      'Create documentation and data governance handoff',
      'Reduce reporting cycles and improve trust in metrics',
    ],
    gradient: 'from-cyan-500 to-sky-500',
  },
  {
    icon: Brain,
    title: 'Digital Asset Creation & AI/GenAI/LLM Governance Strategy & Support',
    description: 'AI is not the problem. Uncontrolled AI is.',
    items: [
      'Define GenAI/AI/LLM governance operating models',
      'Create policies, controls, approval workflows, and oversight roles',
      'Support vendor/model evaluation and risk considerations',
      'Align GenAI/AI/LLM use to privacy, security, and compliance requirements',
      'Implement AI responsibly so it is usable, defensible, and auditable',
    ],
    gradient: 'from-purple-500 to-violet-500',
  },
  {
    icon: Lightbulb,
    title: 'Ideation & Implementation Readiness',
    description: "Good ideas don't get funded without clarity.",
    items: [
      'Shape ideas into clear requirements and scope',
      'Build business cases (value, cost, risk, timeline)',
      'Create roadmaps and implementation-ready plans',
      'Support procurement-ready documentation as needed',
    ],
    gradient: 'from-amber-500 to-orange-500',
  },
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 bg-radial-top" />
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Consulting services designed to improve performance, reduce risk, and strengthen execution—tailored to each organization&apos;s constraints and oversight environment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="p-8 rounded-2xl glass glow-border group"
                data-testid={`service-detail-${index}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-8">
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg`}>
                      <service.icon className="text-white" size={32} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-foreground mb-3 group-hover:text-gradient transition-all">
                      {service.title}
                    </h2>
                    <p className="text-lg text-muted-foreground mb-6">{service.description}</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {service.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <CheckCircle2 className="text-cyan-400 flex-shrink-0 mt-1" size={16} />
                          <span className="text-muted-foreground text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 animate-gradient" />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
            Need help with a <span className="text-gradient">specific challenge?</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Let&apos;s discuss how our services can address your organization&apos;s unique needs.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="group inline-flex items-center px-10 py-5 rounded-xl font-semibold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-all duration-300 shadow-lg shadow-cyan-500/25"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default ServicesPage;
